import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { userSelector } from "../../store/selectors";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import "./SecurityScreen.css";
import ProfileMainIcon from "../../assets/profile-main.svg";
import TextField from "@mui/material/TextField";
import ProfileBillingIcon from "../../assets/profile-billing.svg";
import ProfileNotificationsIcon from "../../assets/profile-notifications.svg";
import ProfileSecurityIcon from "../../assets/profile-security.svg";
import ProfileTeamIcon from "../../assets/users.svg";
import ProfileActiveSessionsIcon from "../../assets/profile-active-sessions.svg";
import Avatar from "../../components/Avatar/Avatar";
import AppLogo from "../../assets/AppLogoBlue.svg";
import toast from "react-hot-toast";
import { updatePassword } from "../../store/usersSlice";

import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import useIsMobile from "../../hooks/useIsMobile";

const SecurityScreen = () => {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const userName = user?.email;

  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showRepeatNewPass, setShowRepeatNewPass] = useState(false);

  const [currentPassValue, setCurrentPassValue] = useState("");
  const [newPassValue, setNewPassValue] = useState("");
  const [repeatNewPassValue, setRepeatNewPassValue] = useState("");

  const [submitAvailable, setSubmitAvailable] = useState(false);
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const inputFieldCx = {
    input: { color: "#F1F7FF" },
    display: "flex",
    width: isMobile ? "100%" : 403,
    fontSize: 15,
    borderRadius: 8,
    fontWeight: 500,
    backgroundColor: "#1f242e",
    // border: "1px solid #333b4b",
    borderRadius: "8px",
    "& .MuiInputBase-root.Mui-focused": {
      border: "1px solid #3496FF",
      borderRadius: "8px",
      boxSizing: "border-box",
    },
    "& .MuiInputBase-root": {
      border: "1px solid #333b4b",
      borderRadius: "8px",
      boxSizing: "border-box",
    },
    "& .MuiInputLabel-root": {
      color: "#717B8E",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#3496FF",
    },
  };

  useEffect(() => {
    document.title = "7cut • Безопасность";
  }, []);

  useEffect(() => {
    if (
      !!currentPassValue &&
      !!newPassValue &&
      !!repeatNewPassValue &&
      newPassValue === repeatNewPassValue
    ) {
      setSubmitAvailable(true);
    } else {
      setSubmitAvailable(false);
    }
  }, [currentPassValue, newPassValue, repeatNewPassValue]);

  const getInputSlotProps = (value, callback) => {
    return {
      input: {
        disableUnderline: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              sx={{ color: "#717B8E", marginRight: "0px" }}
              onClick={() => callback(!value)}
              edge="end"
            >
              {value ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ),
      },
    };
  };

  const handleSaveChanges = () => {
    if (isSaving || !submitAvailable) return;
    setIsSaving(true);
    dispatch(
      updatePassword({
        currentPassword: currentPassValue,
        newPassword: newPassValue,
      })
    ).then((res) => {
      if (!res.error && res.payload !== "Текущий пароль введен неверно.") {
        toast.success("Пароль обновлен");
        setIsSaving(false);
        setSubmitAvailable(false);
        setCurrentPassValue("");
        setNewPassValue("");
        setRepeatNewPassValue("");
      } else if (res.payload === "Текущий пароль введен неверно.") {
        toast.error("Текущий пароль введен неверно.");
        setIsSaving(false);
      } else {
        const errors = res.payload.errors;
        for (let i = 0; i < errors.length; i++) {
          let error = errors[i];
          toast.error(error);
        }

        setIsSaving(false);
      }
    });
  };

  if (!user) return null;

  return (
    <div className={isMobile ? "App-mobile" : "App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "mainScreenHeader"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      {/* LAYOUT: sidebar + main content */}
      <div className="security-container" style={{ alignItems: "flex-start" }}>
        {/* SIDEBAR */}
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="security" />
        )) ||
          null}
        {/* MAIN CONTENT */}
        <main className={isMobile ? "security-main-mobile" : "security-main"}>
          <h1 className="security-title">Безопасность</h1>
          <div className={isMobile ? "security-form-mobile" : "security-form"}>
            <div
              className={
                isMobile ? "security-form-left-mobile" : "security-form-left"
              }
            >
              <TextField
                slotProps={getInputSlotProps(
                  showCurrentPass,
                  setShowCurrentPass
                )}
                sx={inputFieldCx}
                id="filled-basic"
                label="Текущий пароль"
                variant="filled"
                margin="normal"
                type={showCurrentPass ? "text" : "password"}
                value={currentPassValue}
                onChange={(e) => setCurrentPassValue(e.target.value)}
              />
              <TextField
                slotProps={getInputSlotProps(showNewPass, setShowNewPass)}
                sx={inputFieldCx}
                id="filled-basic"
                label="Новый пароль"
                variant="filled"
                margin="normal"
                type={showNewPass ? "text" : "password"}
                value={newPassValue}
                onChange={(e) => setNewPassValue(e.target.value)}
              />
              <TextField
                slotProps={getInputSlotProps(
                  showRepeatNewPass,
                  setShowRepeatNewPass
                )}
                sx={inputFieldCx}
                id="filled-basic"
                label="Подтвердите пароль"
                variant="filled"
                margin="normal"
                type={showRepeatNewPass ? "text" : "password"}
                value={repeatNewPassValue}
                onChange={(e) => setRepeatNewPassValue(e.target.value)}
              />
              <span
                onClick={() => {
                  window.location.href =
                    "https://auth.7cut.video/realms/timeliner/login-actions/reset-credentials?client_id=timeliner-client-4y7nkwz13vb0uqs";
                  // dispatch(resetPassword());
                }}
                className="security-forgot-password"
              >
                Забыли пароль?
              </span>
              <button
                onClick={handleSaveChanges}
                disabled={!submitAvailable}
                className={`security-tariff-btn ${
                  !submitAvailable && "security-tariff-btn-active"
                }`}
              >
                {isSaving ? "Сохраняется..." : "Подтвердить"}
              </button>
            </div>

            <div className="security-form-right"></div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SecurityScreen;
